import apiClient from '@/shared/services/apiClient';

// --- Public tracking ---

const SESSION_KEY = 'analytics_session_id';
const LAST_ACTIVITY_KEY = 'analytics_last_activity';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

function generateUUID(): string {
  return crypto.randomUUID();
}

export function getSessionId(): string {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  const existing = localStorage.getItem(SESSION_KEY);

  if (existing && lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    if (elapsed < SESSION_TIMEOUT) {
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      return existing;
    }
  }

  // New session
  const newId = generateUUID();
  localStorage.setItem(SESSION_KEY, newId);
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  return newId;
}

export async function trackPageView(data: {
  page_path: string;
  page_title?: string;
  referrer?: string;
}): Promise<{ page_view_id: number; visitor_id: number } | null> {
  try {
    const res = await apiClient.post('/track/pageview', {
      ...data,
      session_id: getSessionId(),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
    });
    return res.data;
  } catch {
    return null;
  }
}

export function sendTimeOnPage(pageViewId: number, timeOnPage: number): void {
  const data = JSON.stringify({
    page_view_id: pageViewId,
    time_on_page: Math.round(timeOnPage / 1000), // ms → seconds
  });
  navigator.sendBeacon('/api/v1/track/time', new Blob([data], { type: 'application/json' }));
}

// --- Admin dashboard ---

export interface DashboardStats {
  total_views: number;
  unique_visitors: number;
  total_sessions: number;
  avg_time_on_page: number;
  realtime_visitors: number;
  period: string;
  period_start: string;
  period_end: string;
  top_pages: { path: string; views: number; unique_visitors: number }[];
  countries: { country: string; visitors: number; views: number }[];
  cities: { city: string; country: string; visitors: number }[];
  devices: { device: string; sessions: number }[];
  browsers: { browser: string; sessions: number }[];
  operating_systems: { os: string; sessions: number }[];
  referrers: { referrer: string; views: number }[];
  hourly: { hour: string; views: number }[];
}

export async function getDashboardStats(period = 'week'): Promise<DashboardStats> {
  const res = await apiClient.get('/admin/analytics/dashboard', { params: { period } });
  return res.data;
}

export interface PageViewLogEntry {
  id: number;
  page_path: string;
  page_title: string | null;
  referrer: string | null;
  time_on_page: number | null;
  viewed_at: string;
  visitor_id: number;
  ip_address: string;
  country: string | null;
  city: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  user_id: number | null;
  username: string | null;
}

export async function getPageViewLog(params: {
  page?: number;
  per_page?: number;
  path?: string;
  country?: string;
  device?: string;
  date_from?: string;
  date_to?: string;
}): Promise<{ items: PageViewLogEntry[]; total: number; page: number; per_page: number }> {
  const res = await apiClient.get('/admin/analytics/log', { params });
  return res.data;
}

export interface VisitorDetail {
  visitor: {
    id: number;
    fingerprint_hash: string;
    ip_address: string;
    country: string | null;
    city: string | null;
    first_seen_at: string | null;
    last_seen_at: string | null;
    visit_count: number;
    is_bot: boolean;
    cookies: string | null;
  };
  sessions: {
    id: number;
    session_id: string;
    started_at: string | null;
    last_activity_at: string | null;
    device_type: string | null;
    browser: string;
    os: string;
    page_view_count: number;
  }[];
  page_views: {
    id: number;
    page_path: string;
    page_title: string | null;
    referrer: string | null;
    time_on_page: number | null;
    viewed_at: string | null;
    user_id: number | null;
    username: string | null;
  }[];
}

export async function getVisitorDetail(visitorId: number): Promise<VisitorDetail> {
  const res = await apiClient.get(`/admin/analytics/visitors/${visitorId}`);
  return res.data;
}

export async function purgeAnalytics(days = 90): Promise<{ deleted_page_views: number }> {
  const res = await apiClient.post('/admin/analytics/purge', null, { params: { days } });
  return res.data;
}

// --- Activity stats ---

export interface EmailProviderStats {
  name: string;
  sent_hour: number;
  sent_today: number;
  sent_month: number;
  failed_today: number;
  failed_month: number;
  hourly_limit: number | null;
  daily_limit: number | null;
  monthly_limit: number | null;
}

export interface ActivityStats {
  period: string;
  registrations: number;
  logins: number;
  comments: number;
  comments_pending: number;
  emails_sent: number;
  daily: {
    date: string;
    registrations: number;
    logins: number;
    comments: number;
    emails: number;
  }[];
  email_providers: {
    providers: EmailProviderStats[];
    total_sent_today: number;
    total_sent_month: number;
  };
}

export async function getActivityStats(period = 'week'): Promise<ActivityStats> {
  const res = await apiClient.get('/admin/analytics/activity', { params: { period } });
  return res.data;
}
